/**
 * @file Pricing Grid — Elevated
 * Psychology techniques applied:
 * 1. Price anchoring: monthly price shown first (lower number = less sticker shock)
 * 2. "From" framing: "da €14,99/mese" makes it feel accessible
 * 3. PRO card is 1.4× wider + taller — size = perceived value (size heuristic)
 * 4. Loss aversion: on monthly toggle, a yellow bar says "Stai lasciando €X sul tavolo"
 * 5. Decoy effect: lite makes Pro look reasonable by contrast
 * 6. Social proof + specificity: "500+ agenti", "+40% produttività media"
 * 7. Annual: strike-through on old price + green savings reinforcement
 */

'use client';

import { useState } from 'react';
import { PricingCardAction } from "./pricing-card-action";
import {
  Check, X, Shield, Sparkles, Gift,
  Users, TrendingUp, Star, AlertTriangle
} from "lucide-react";

const plans = {
  lite:   { monthly: 12.99, annual: 132 },
  medium: { monthly: 18.99, annual: 192 },
  pro:    { monthly: 34.99, annual: 360 },
};

const fmt = (n: number) =>
  n.toFixed(2).replace('.', ',');

export function PricingGrid() {
  const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly');
  const [fading, setFading] = useState(false);

  const handleCycle = (next: 'monthly' | 'annual') => {
    if (next === cycle) return;
    setFading(true);
    setTimeout(() => { setCycle(next); setFading(false); }, 150);
  };

  const stdSavings  = fmt(plans.lite.monthly * 12 - plans.lite.annual);
  const medSavings  = fmt(plans.medium.monthly * 12 - plans.medium.annual);
  const proSavings  = fmt(plans.pro.monthly * 12 - plans.pro.annual);
  const stdMonthly  = fmt(plans.lite.annual / 12);
  const medMonthly  = fmt(plans.medium.annual / 12);
  const proMonthly  = fmt(plans.pro.annual / 12);

  const amountCls = `transition-all duration-150 ${fading ? 'opacity-0 translate-y-1' : 'opacity-100'}`;

  return (
    <div className="max-w-5xl mx-auto mb-24 px-4">

      {/* ── TOGGLE AREA ───────────────────────────────────── */}
      <div className="text-center mb-10">

        {/* Social proof banner */}
        <div className="inline-block bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 mb-6 max-w-lg">
          <p className="flex items-center justify-center gap-2 text-emerald-700 font-semibold text-sm mb-1">
            <Gift className="h-4 w-4" />
            La scelta preferita dai professionisti
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            L'82% dei clienti sceglie l'annuale per risparmiare{' '}
            <strong className="text-emerald-700">fino a €{proSavings}</strong>{' '}
            e non pensarci più per 12 mesi.
          </p>
        </div>

        {/* Labels */}
        <div className="flex items-center justify-center gap-6 mb-3 text-xs font-medium">
          <span className={cycle === 'monthly' ? 'text-slate-900' : 'text-slate-400'}>
            Massima flessibilità
          </span>
          <span className={cycle === 'annual' ? 'text-slate-900' : 'text-slate-400'}>
            Massimo valore
          </span>
        </div>

        {/* Toggle */}
        <div className="inline-flex bg-slate-100 p-1 rounded-xl gap-1">
          <button
            onClick={() => handleCycle('monthly')}
            className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
              cycle === 'monthly'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-400 hover:text-slate-700'
            }`}
          >
            Mensile
          </button>
          <button
            onClick={() => handleCycle('annual')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${
              cycle === 'annual'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-400 hover:text-slate-700'
            }`}
          >
            Annuale
            <span className="bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-md uppercase tracking-wider font-bold">
              -15%
            </span>
          </button>
        </div>

        {/* Annual savings pill */}
        {cycle === 'annual' && (
          <div className="mt-3 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold px-4 py-2 rounded-full animate-in fade-in slide-in-from-top-2 duration-300">
            <Sparkles className="h-3.5 w-3.5" />
            Ottima scelta — risparmi <span className="font-black mx-0.5 text-emerald-600">€{proSavings}</span> all'anno
          </div>
        )}

        {/* Loss aversion banner — only on monthly */}
        {cycle === 'monthly' && (
          <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold px-4 py-2 rounded-full animate-in fade-in duration-300">
            <AlertTriangle className="h-3.5 w-3.5" />
            Stai lasciando €{proSavings} sul tavolo — passa all'annuale e risparmia.
          </div>
        )}
      </div>

      {/* ── PRICING GRID ──────────────────────────────────── */}
      {/*
        Layout strategy:
        - lite + Agency: normal height, border on outer sides only
        - Pro: 1.4× width, taller (extra padding top/bottom), z-10, full border ring
        - Grid uses cols [1fr 1.4fr 1fr] + align-items: end so outer cards sit lower
      */}
      <div
        className="grid items-end"
        style={{ gridTemplateColumns: '1fr 1.4fr 1fr' }}
      >

        {/* ── lite ────────────────────────────────────── */}
        <div className="bg-white border-2 border-slate-200 border-r-0 rounded-l-2xl p-6 flex flex-col h-full hover:bg-slate-50 transition-colors">
          <p className="text-[11px] font-black tracking-widest text-slate-400 uppercase mb-3">lite</p>

          {/* Price — monthly anchor shown first regardless */}
          <p className="text-[10px] text-slate-400 mb-0.5">
            {cycle === 'annual' ? 'fatturato annualmente' : 'da'}
          </p>
          <div className={`flex items-baseline gap-1 ${amountCls}`}>
            <span className="text-2xl font-semibold text-slate-700">€</span>
            <span className="text-5xl font-black text-slate-900 leading-none tabular-nums">
              {cycle === 'monthly'
                ? fmt(plans.lite.monthly)
                : fmt(plans.lite.annual)}
            </span>
            <span className="text-slate-500 text-sm self-end mb-1">
              /{cycle === 'monthly' ? 'mese' : 'anno'}
            </span>
          </div>

          {cycle === 'annual' && (
            <div className="mt-2 space-y-1 animate-in fade-in duration-300">
              <p className="text-emerald-700 font-bold text-xs">
                €{stdMonthly}/mese — risparmi €{stdSavings}/anno
              </p>
              <p className="text-slate-400 text-xs line-through">€{fmt(plans.lite.monthly * 12)} / anno</p>
            </div>
          )}

          <p className="text-slate-500 text-xs leading-relaxed mt-3 mb-4">
            Per organizzare clienti e ordini senza complessità.
          </p>

          <p className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-4">
            <Users className="h-3.5 w-3.5" /> 500+ agenti attivi
          </p>

          <PricingCardAction
            tier="lite"
            label="Inizia con lite"
            highlighted={false}
            cycle={cycle}
          />

          <hr className="border-slate-100 my-4" />

          <ul className="space-y-2.5 text-xs text-slate-600 flex-1">
            {[
              'Clienti e Mandanti illimitati',
              'Ordini e fatture illimitati',
              'Mappa e analisi geografica',
            ].map(f => (
              <li key={f} className="flex gap-2 items-start">
                <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{f}
              </li>
            ))}
            <li className="flex gap-2 items-start opacity-40">
              <X className="h-4 w-4 shrink-0 mt-0.5" />Nessuna automazione AI
            </li>
          </ul>
        </div>

        {/* ── PRO AGENT — featured, larger ────────────────── */}
        <div className="relative bg-white border-2 border-indigo-600 rounded-2xl px-8 pt-10 pb-8 flex flex-col z-10 shadow-2xl shadow-indigo-100">

          {/* "Most popular" badge */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
            <span className="flex items-center gap-1.5 bg-indigo-600 text-white text-[11px] font-black uppercase tracking-widest px-5 py-1.5 rounded-full shadow-lg">
              <Star className="h-3 w-3 fill-white" /> Più Scelto
            </span>
          </div>

          {/* Subtle indigo glow behind card */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-indigo-50/60 to-transparent pointer-events-none" />

          <div className="relative">
            <p className="text-[11px] font-black tracking-widest text-indigo-500 uppercase mb-3">Pro Agent</p>

            {/* Price — monthly shown first */}
            <p className="text-[10px] text-slate-400 mb-0.5">
              {cycle === 'annual' ? 'fatturato annualmente' : 'da'}
            </p>
            <div className={`flex items-baseline gap-1 ${amountCls}`}>
              <span className="text-3xl font-semibold text-indigo-900">€</span>
              <span className="text-7xl font-black text-indigo-900 leading-none tabular-nums" style={{letterSpacing: '-3px'}}>
                {cycle === 'monthly'
                  ? fmt(plans.pro.monthly)
                  : fmt(plans.pro.annual)}
              </span>
              <span className="text-slate-500 text-sm self-end mb-1">
                /{cycle === 'monthly' ? 'mese' : 'anno'}
              </span>
            </div>

            {cycle === 'annual' && (
              <div className="mt-2 space-y-1 animate-in fade-in duration-300">
                <span className="inline-block bg-emerald-100 text-emerald-700 font-bold text-xs px-3 py-1 rounded-full">
                  €{proMonthly}/mese — risparmi €{proSavings}/anno
                </span>
                <p className="text-slate-400 text-xs line-through mt-1">€{fmt(plans.pro.monthly * 12)} / anno</p>
              </div>
            )}

            <p className="text-slate-700 text-sm leading-relaxed mt-4 mb-4">
              Per chi vuole vendere di più, perdere meno tempo e{' '}
              <span className="text-indigo-700 font-semibold">lasciare che l'AI lavori al posto suo</span>.
            </p>

            <p className="flex items-center gap-1.5 text-xs font-semibold text-indigo-500 mb-5">
              <TrendingUp className="h-4 w-4" /> +40% produttività media
            </p>

            <PricingCardAction
              tier="pro"
              label="Inizia con Pro Agent"
              highlighted
              cycle={cycle}
            />

            <hr className="border-indigo-100 my-5" />

            <ul className="space-y-3 text-sm text-slate-800 flex-1">
              {[
                'Lite/Medium tutto incluso',
                'Importi fatture e ordini direttamente in PDF',
                'Registri le visite, l\'AI fa il resto',
                'Trascrizione automatica',
                'Task intelligenti pronti',
                'Giro Visite potenziato',
                'Discovery Leads con AI',
                'Supporto prioritario',
              ].map(f => (
                <li key={f} className="flex gap-2.5 items-start font-medium">
                  <Check className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />{f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── MEDIUM ──────────────────────────────────── */}
        <div className="bg-white border-2 border-slate-200 border-l-0 rounded-r-2xl p-6 flex flex-col h-full hover:bg-slate-50 transition-colors">
          <p className="text-[11px] font-black tracking-widest text-slate-400 uppercase mb-3">Medium</p>

          {/* Price — monthly anchor shown first regardless */}
          <p className="text-[10px] text-slate-400 mb-0.5">
            {cycle === 'annual' ? 'fatturato annualmente' : 'da'}
          </p>
          <div className={`flex items-baseline gap-1 ${amountCls}`}>
            <span className="text-2xl font-semibold text-slate-700">€</span>
            <span className="text-5xl font-black text-slate-900 leading-none tabular-nums">
              {cycle === 'monthly'
                ? fmt(plans.medium.monthly)
                : fmt(plans.medium.annual)}
            </span>
            <span className="text-slate-500 text-sm self-end mb-1">
              /{cycle === 'monthly' ? 'mese' : 'anno'}
            </span>
          </div>

          {cycle === 'annual' && (
            <div className="mt-2 space-y-1 animate-in fade-in duration-300">
              <p className="text-emerald-700 font-bold text-xs">
                €{medMonthly}/mese — risparmi €{medSavings}/anno
              </p>
              <p className="text-slate-400 text-xs line-through">€{fmt(plans.medium.monthly * 12)} / anno</p>
            </div>
          )}

          <p className="text-slate-500 text-xs leading-relaxed mt-3 mb-4">
            Per chi vuole iniziare ad automatizzare con l'AI senza investire troppo.
          </p>

          <p className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-4">
            <Users className="h-3.5 w-3.5" /> 300+ agenti attivi
          </p>

          <PricingCardAction
            tier="medium"
            label="Inizia con Medium"
            highlighted={false}
            cycle={cycle}
          />

          <hr className="border-slate-100 my-4" />

          <ul className="space-y-2.5 text-xs text-slate-600 flex-1">
            {[
              'Tutto Lite incluso',
              'Import fatture e ordini da PDF',
              'Trascrizione automatica',
              'Task intelligenti AI',
              'Briefing clienti AI',
            ].map(f => (
              <li key={f} className="flex gap-2 items-start">
                <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{f}
              </li>
            ))}
            <li className="flex gap-2 items-start opacity-40">
              <X className="h-4 w-4 shrink-0 mt-0.5" />Discovery Leads con AI
            </li>
          </ul>
        </div>
      </div>

      {/* ── GUARANTEE ROW ─────────────────────────────────── */}
      <div className="mt-10 space-y-3">
        <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-emerald-500" />30 giorni soddisfatti o rimborsati</span>
          <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" />Cambia piano quando vuoi</span>
          <span className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-emerald-500" />14 giorni di prova gratuiti</span>
        </div>
        <p className="text-[11px] text-slate-300 text-center">
          I prezzi sono in euro IVA esclusa. Sono possibili variazioni per utenti con esigenze specifiche.
        </p>
      </div>
    </div>
  );
}